import React from 'react';
import styled from 'styled-components';
import Transition from 'components/Transition';
import { Backdrop } from '../Modal/modalStyle';

/* ── Brand tokens ────────────────────────────────────────────────────
   Primary:  #600b68  (plum)
   Dark:     #4a0852
   Magenta:  #b8357b
   Teal:     #4dadb3
   Yellow:   #ffbc50
   Accent bg:#efdbf1
   Text:     #000000 / #374151 / #6b6b6b
   Font:     'Helvetica Neue', Helvetica, Arial, sans-serif
──────────────────────────────────────────────────────────────────── */

const FONT = "'Helvetica Neue', Helvetica, Arial, sans-serif";

/* ── Shell ─────────────────────────────────────────────────────────── */

const Sheet = styled.div`
  position: relative;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.35);
  width: 94vw;
  max-width: 720px;
  max-height: 92vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  font-family: ${FONT};
`;

const CloseBtn = styled.button`
  position: sticky;
  top: 0;
  align-self: flex-end;
  margin: 1rem 1rem 0;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #d0d0d0;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  font-size: 1.8rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  flex-shrink: 0;
  z-index: 10;
  font-family: ${FONT};

  &:hover { background: #efdbf1; }
`;

/* ── Hero ───────────────────────────────────────────────────────────── */

const Hero = styled.section`
  background: #600b68;
  padding: 2.5rem 2.5rem 2rem;
`;

const HeroLogo = styled.img`
  height: 28px;
  width: auto;
  display: block;
  margin-bottom: 1.25rem;
  opacity: 0.9;
`;

const Eyebrow = styled.p`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #efdbf1;
  margin: 0 0 0.75rem;
  font-family: ${FONT};
`;

const HeroTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #fff;
  line-height: 1.25;
  margin: 0 0 1rem;
  font-family: ${FONT};
`;

const HeroBody = styled.p`
  font-size: 1.05rem;
  line-height: 1.75;
  color: #efdbf1;
  margin: 0;
  font-family: ${FONT};
`;

/* ── Body ───────────────────────────────────────────────────────────── */

const Body = styled.div`
  padding: 2rem 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.section``;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: #000;
  margin: 0 0 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #efdbf1;
  font-family: ${FONT};
`;

const Para = styled.p`
  font-size: 1rem;
  line-height: 1.75;
  color: #374151;
  margin: 0 0 0.75rem;
  font-family: ${FONT};

  &:last-child { margin-bottom: 0; }
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0;

  li {
    position: relative;
    padding-left: 1.25rem;
    font-size: 1rem;
    line-height: 1.7;
    color: #374151;
    margin-bottom: 0.35rem;
    font-family: ${FONT};

    &::before {
      content: '▸';
      position: absolute;
      left: 0;
      color: #b8357b;
      font-size: 0.75rem;
      top: 0.25rem;
    }
  }
`;

/* ── CTA / link cards ───────────────────────────────────────────────── */

const CtaSection = styled.section`
  background: #efdbf1;
  border-top: 1px solid rgba(96, 11, 104, 0.15);
  padding: 2rem 2.5rem 2.5rem;
  border-radius: 0 0 12px 12px;
`;

const CtaTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: #000;
  margin: 0 0 0.5rem;
  font-family: ${FONT};
`;

const CtaBody = styled.p`
  font-size: 1rem;
  line-height: 1.75;
  color: #374151;
  margin: 0 0 1.5rem;
  font-family: ${FONT};
`;

const LinkCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const LinkCard = styled.a`
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(96, 11, 104, 0.2);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  text-decoration: none;
  color: inherit;
  background: #fff;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #fff;
    border-color: #600b68;
    box-shadow: 0 2px 8px rgba(96, 11, 104, 0.12);
  }
`;

const LinkCardLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #b8357b;
  margin-bottom: 0.2rem;
  font-family: ${FONT};
`;

const LinkCardTitle = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: #000;
  margin-bottom: 0.25rem;
  font-family: ${FONT};
`;

const LinkCardDesc = styled.span`
  font-size: 0.925rem;
  color: #6b6b6b;
  line-height: 1.55;
  font-family: ${FONT};
`;

/* ─────────────────────────────────────────────────────────────────── */

interface Props {
  onClose: () => void;
}

export default function AboutIntegralEdModal({ onClose }: Props) {
  return (
    <Backdrop onClick={onClose}>
      <Transition className="fade" enter timeout={400}>
        <Sheet onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <CloseBtn onClick={onClose} aria-label="Close">×</CloseBtn>

          {/* ── Hero ─────────────────────────────────────────── */}
          <Hero>
            <HeroLogo
              src={`${process.env.PUBLIC_URL}/media/images/ie-logo.png`}
              alt="Integral Ed"
            />
            <Eyebrow>About Integral Ed</Eyebrow>
            <HeroTitle>Chat-based learning that moves beyond conversation</HeroTitle>
            <HeroBody>
              Career Ikigai is an example of how we design chat-based learning experiences that move
              beyond conversation and into clear, actionable outputs.
            </HeroBody>
          </Hero>

          {/* ── Body ─────────────────────────────────────────── */}
          <Body>

            <Section>
              <Para>
                Learners engage in a guided interview that surfaces:
              </Para>
              <BulletList>
                <li>interests and motivations</li>
                <li>strengths and past experiences</li>
                <li>preferences around environment, pace, and purpose</li>
              </BulletList>
              <Para style={{ marginTop: '0.75rem' }}>
                The system then synthesizes those inputs into personalized recommendations and a
                structured output that can be used immediately in advising, counseling, or program
                settings.
              </Para>
            </Section>

            <Section>
              <SectionTitle>From interview to editable deliverable</SectionTitle>
              <Para>
                Unlike typical chat tools, the goal is not just a response. It is a shareable
                artifact.
              </Para>
              <Para>Each session produces:</Para>
              <BulletList>
                <li>a structured summary of the learner's direction</li>
                <li>suggested pathways and next steps</li>
                <li>a formatted slide output that can be edited and refined</li>
                <li>a document that can be shared with advisors, mentors, or program staff</li>
              </BulletList>
              <Para style={{ marginTop: '0.75rem' }}>
                This creates continuity between reflection, conversation, and real decision-making.
              </Para>
            </Section>

            <Section>
              <SectionTitle>Built for advising, mentoring, and program use</SectionTitle>
              <Para>
                Career Ikigai is designed to fit inside real systems, not sit outside them.
              </Para>
              <Para>In practice, organizations can:</Para>
              <BulletList>
                <li>share learner outputs directly with advisors or counselors</li>
                <li>store results in program Google Drive folders</li>
                <li>connect outputs to dashboards for cohort-level insights</li>
                <li>identify patterns, needs, and opportunities across participants</li>
              </BulletList>
              <Para style={{ marginTop: '0.75rem' }}>
                This allows teams to move from individual conversations to program-level visibility.
              </Para>
            </Section>

            <Section>
              <SectionTitle>Personalized recommendations grounded in real data</SectionTitle>
              <Para>The underlying system can connect to:</Para>
              <BulletList>
                <li>localized job market data</li>
                <li>workforce demand signals</li>
                <li>regional pathway opportunities</li>
              </BulletList>
              <Para style={{ marginTop: '0.75rem' }}>
                This allows recommendations to reflect real options available to the learner, not
                generic suggestions.
              </Para>
            </Section>

            <Section>
              <SectionTitle>A model for personalized learning at scale</SectionTitle>
              <Para>
                This example illustrates a broader approach to eLearning and blended learning design
                at Integral Ed.
              </Para>
              <Para>We combine:</Para>
              <BulletList>
                <li>structured, scenario-based interaction</li>
                <li>conversational interfaces</li>
                <li>data-informed personalization</li>
                <li>tangible outputs that support real-world use</li>
              </BulletList>
              <Para style={{ marginTop: '0.75rem' }}>The result is learning that is:</Para>
              <BulletList>
                <li>individualized</li>
                <li>actionable</li>
                <li>connected to real systems and decisions</li>
              </BulletList>
            </Section>

            <Section>
              <SectionTitle>How this connects to our work</SectionTitle>
              <Para>Career Ikigai sits at the intersection of:</Para>
              <BulletList>
                <li>eLearning and blended learning</li>
                <li>career-connected learning</li>
                <li>advising and mentoring systems</li>
                <li>platform and workflow design</li>
              </BulletList>
              <Para style={{ marginTop: '0.75rem' }}>
                It reflects how we help organizations translate complex guidance processes into
                usable digital experiences.
              </Para>
            </Section>

          </Body>

          {/* ── CTA ──────────────────────────────────────────── */}
          <CtaSection>
            <CtaTitle>Interested in building something similar?</CtaTitle>
            <CtaBody>
              We work with organizations to design and deploy custom learning tools like this,
              tailored to their audiences, data, and goals.
            </CtaBody>
            <LinkCards>
              <LinkCard href="https://www.integralelearning.com/" target="_blank" rel="noreferrer">
                <LinkCardLabel>eLearning &amp; Blended Learning</LinkCardLabel>
                <LinkCardTitle>Integral eLearning</LinkCardTitle>
                <LinkCardDesc>
                  Custom eLearning development, instructional design, and media for education
                  organizations.
                </LinkCardDesc>
              </LinkCard>
              <LinkCard href="https://www.integralcareerpathways.com/" target="_blank" rel="noreferrer">
                <LinkCardLabel>Career-Connected Learning</LinkCardLabel>
                <LinkCardTitle>Integral Career Pathways</LinkCardTitle>
                <LinkCardDesc>
                  Tools, programs, and systems that connect learners to real pathways and
                  opportunities.
                </LinkCardDesc>
              </LinkCard>
              <LinkCard href="https://www.integral-ed.com/" target="_blank" rel="noreferrer">
                <LinkCardLabel>Main Site</LinkCardLabel>
                <LinkCardTitle>Integral Ed Services</LinkCardTitle>
                <LinkCardDesc>
                  An educator-led learning design firm based in Austin, TX. Serving education
                  organizations since 2011.
                </LinkCardDesc>
              </LinkCard>
            </LinkCards>
          </CtaSection>

        </Sheet>
      </Transition>
    </Backdrop>
  );
}
